export const toBool = (value, fallback = false) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['1', 'true', 'yes', 'on'].includes(normalized)) return true;
    if (['0', 'false', 'no', 'off'].includes(normalized)) return false;
  }
  return Boolean(fallback);
};

export const toFiniteCount = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'boolean') return null;
  if (typeof value === 'string' && !value.trim()) return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  return Math.max(0, Math.trunc(parsed));
};

export const toFiniteNumber = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'boolean') return null;
  if (typeof value === 'string' && !value.trim()) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const normalizeConfidenceScore = (value) => {
  const parsed = toFiniteNumber(value);
  if (parsed === null) return null;
  let normalized = parsed;
  if (normalized > 1 && normalized <= 100) {
    normalized = normalized / 100;
  }
  if (normalized < 0) normalized = 0;
  if (normalized > 1) normalized = 1;
  return Number(normalized.toFixed(4));
};

export const formatConfidencePercent = (value, fallback = 'N/A') => {
  const normalized = normalizeConfidenceScore(value);
  if (normalized === null) return fallback;
  return `${(normalized * 100).toFixed(1)}%`;
};

const normalizePriceBound = (value) => {
  const parsed = toFiniteNumber(value);
  if (parsed === null) return null;
  const normalized = parsed < 0 ? 0 : parsed;
  return Number(normalized.toFixed(2));
};

const extractRangeFromCandidate = (candidate) => {
  if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
    let low = normalizePriceBound(
      candidate.low ?? candidate.min ?? candidate.from ?? candidate.start
    );
    let high = normalizePriceBound(
      candidate.high ?? candidate.max ?? candidate.to ?? candidate.end
    );
    if (low === null) {
      low = normalizePriceBound(candidate.estimated_value_low ?? candidate.estimated_low);
    }
    if (high === null) {
      high = normalizePriceBound(candidate.estimated_value_high ?? candidate.estimated_high);
    }
    return { low, high };
  }
  if (Array.isArray(candidate) && candidate.length >= 2) {
    return {
      low: normalizePriceBound(candidate[0]),
      high: normalizePriceBound(candidate[1]),
    };
  }
  return { low: null, high: null };
};

export const normalizeEstimatedValueRange = (value) => {
  if (!value || typeof value !== 'object') return null;

  let low = null;
  let high = null;
  const rangeCandidates = [
    value.estimated_value_range,
    value.estimated_range,
    value.price_range,
    value.estimatedValueRange,
    value.value_range,
  ];
  for (const candidate of rangeCandidates) {
    const parsed = extractRangeFromCandidate(candidate);
    if (parsed.low !== null || parsed.high !== null) {
      low = parsed.low;
      high = parsed.high;
      break;
    }
  }

  if (low === null) {
    low = normalizePriceBound(value.estimated_value_low ?? value.estimated_low ?? value.price_low);
  }
  if (high === null) {
    high = normalizePriceBound(value.estimated_value_high ?? value.estimated_high ?? value.price_high);
  }

  if (low === null && high === null) return null;
  if (low === null) low = high;
  if (high === null) high = low;
  if (low === null || high === null) return null;
  if (high < low) {
    return { low: high, high: low };
  }
  return { low, high };
};

const normalizeProbability = (value) => {
  const parsed = toFiniteNumber(value);
  if (parsed === null) return null;
  let normalized = parsed;
  if (normalized > 1 && normalized <= 100) {
    normalized = normalized / 100;
  }
  if (normalized < 0) normalized = 0;
  if (normalized > 1) normalized = 1;
  return Number(normalized.toFixed(6));
};

const normalizeDistributionEntry = (entry) => {
  let low = null;
  let high = null;
  let probability = null;

  if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
    low = normalizePriceBound(entry.low ?? entry.min ?? entry.from ?? entry.start);
    high = normalizePriceBound(entry.high ?? entry.max ?? entry.to ?? entry.end);
    if (low === null) {
      low = normalizePriceBound(entry.estimated_value_low ?? entry.estimated_low);
    }
    if (high === null) {
      high = normalizePriceBound(entry.estimated_value_high ?? entry.estimated_high);
    }
    if (low === null && high === null) {
      const pointEstimate = normalizePriceBound(entry.value ?? entry.price ?? entry.estimated_value);
      if (pointEstimate !== null) {
        low = pointEstimate;
        high = pointEstimate;
      }
    }
    probability = normalizeProbability(entry.probability ?? entry.prob ?? entry.weight);
  } else if (Array.isArray(entry)) {
    if (entry.length >= 3) {
      low = normalizePriceBound(entry[0]);
      high = normalizePriceBound(entry[1]);
      probability = normalizeProbability(entry[2]);
    } else if (entry.length >= 2) {
      const pointEstimate = normalizePriceBound(entry[0]);
      low = pointEstimate;
      high = pointEstimate;
      probability = normalizeProbability(entry[1]);
    }
  }

  if (probability === null) return null;
  if (low === null && high === null) return null;
  if (low === null) low = high;
  if (high === null) high = low;
  if (low === null || high === null) return null;
  if (high < low) {
    return { low: high, high: low, probability };
  }
  return { low, high, probability };
};

export const normalizePriceEstimateDistribution = (value) => {
  if (!value || typeof value !== 'object') return [];

  let rawDistribution = null;
  const distributionCandidates = [
    value.price_estimate_distribution,
    value.estimated_value_distribution,
    value.price_distribution,
    value.estimated_distribution,
    value.estimatedValueDistribution,
    value.distribution,
  ];
  for (const candidate of distributionCandidates) {
    if (!candidate) continue;
    if (Array.isArray(candidate)) {
      rawDistribution = candidate;
      break;
    }
    if (candidate && typeof candidate === 'object') {
      const nested = candidate.bins ?? candidate.distribution ?? candidate.values ?? candidate.items;
      if (Array.isArray(nested)) {
        rawDistribution = nested;
        break;
      }
      if (
        ['low', 'high', 'min', 'max', 'value', 'price', 'probability', 'prob']
          .some((key) => Object.prototype.hasOwnProperty.call(candidate, key))
      ) {
        rawDistribution = [candidate];
        break;
      }
    }
  }
  if (!Array.isArray(rawDistribution)) return [];

  const normalizedEntries = rawDistribution
    .map((entry) => normalizeDistributionEntry(entry))
    .filter((entry) => entry !== null);
  const totalProbability = normalizedEntries.reduce((sum, entry) => sum + entry.probability, 0);
  if (!(totalProbability > 0)) return [];

  const normalized = normalizedEntries.map((entry) => ({
    low: entry.low,
    high: entry.high,
    probability: Number((entry.probability / totalProbability).toFixed(6)),
  }));
  if (normalized.length === 1) {
    normalized[0].probability = 1;
  } else if (normalized.length > 1) {
    const leadingSum = normalized
      .slice(0, -1)
      .reduce((sum, entry) => sum + entry.probability, 0);
    normalized[normalized.length - 1].probability = Number(Math.max(0, 1 - leadingSum).toFixed(6));
  }
  return normalized;
};

export const enrichPriceEstimateFields = (item) => {
  if (!item || typeof item !== 'object') return item;
  const estimatedValueRange = normalizeEstimatedValueRange(item);
  const priceEstimateDistribution = normalizePriceEstimateDistribution(item);
  const derivedRange = estimatedValueRange || (
    priceEstimateDistribution.length > 0
      ? {
          low: Math.min(...priceEstimateDistribution.map((entry) => entry.low)),
          high: Math.max(...priceEstimateDistribution.map((entry) => entry.high)),
        }
      : null
  );
  return {
    ...item,
    estimated_value_low: derivedRange?.low ?? null,
    estimated_value_high: derivedRange?.high ?? null,
    estimated_value_range: derivedRange,
    price_estimate_distribution: priceEstimateDistribution,
  };
};

export const formatEstimatedValueRange = (rangeValue, fallback = 'N/A') => {
  const normalized = normalizeEstimatedValueRange({ estimated_value_range: rangeValue }) || normalizeEstimatedValueRange(rangeValue);
  if (!normalized) return fallback;
  return `$${normalized.low.toFixed(2)} - $${normalized.high.toFixed(2)}`;
};

export const formatPriceEstimateDistribution = (distributionValue, fallback = 'N/A', maxBins = 3) => {
  const normalized = normalizePriceEstimateDistribution({ price_estimate_distribution: distributionValue });
  if (!normalized.length) return fallback;
  const capped = normalized.slice(0, Math.max(1, maxBins));
  const binsText = capped.map((entry) => {
    const rangeText = `$${entry.low.toFixed(0)}-$${entry.high.toFixed(0)}`;
    const probabilityText = `${(entry.probability * 100).toFixed(0)}%`;
    return `${rangeText}:${probabilityText}`;
  }).join(', ');
  return normalized.length > capped.length ? `${binsText}, ...` : binsText;
};

export const formatResizeDisplay = (mode) => {
  const normalized = String(mode || '').trim().toLowerCase();
  if (!normalized || normalized === 'n/a') return 'N/A';
  return normalized === 'original' ? 'none' : normalized;
};

const normalizeIsoUtcInput = (value) => {
  if (value === null || value === undefined) return '';
  const text = String(value).trim();
  if (!text) return '';
  if (/[zZ]$/.test(text) || /[+-]\d{2}:\d{2}$/.test(text)) {
    return text;
  }
  return `${text}Z`;
};

export const formatStartedAt = (value) => {
  const normalized = normalizeIsoUtcInput(value);
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return 'In progress';
  return parsed.toISOString().replace('T', ' ').replace('Z', ' UTC');
};

export const formatTokenCount = (value) => {
  const parsed = toFiniteCount(value);
  if (parsed === null) return 'N/A';
  return parsed.toLocaleString();
};

export const formatEta = (valueSeconds) => {
  const totalSeconds = toFiniteNumber(valueSeconds);
  if (totalSeconds === null || totalSeconds < 0) return 'N/A';
  if (totalSeconds === 0) return '0s';
  const seconds = Math.round(totalSeconds);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${secs}s`;
  return `${secs}s`;
};

export const formatExportTimestamp = (value) => {
  const normalized = normalizeIsoUtcInput(value);
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString();
};

export const getItemExplanation = (item = {}) => {
  const candidates = [
    item?.explanation,
    item?.margin_analysis,
    item?.marginAnalysis,
    item?.price_explanation,
    item?.price_reasoning,
    item?.reasoning,
  ];
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }
  }
  return '';
};

export const getFileExtension = (filename = '') => {
  const splitName = String(filename).split('.');
  if (splitName.length < 2) return '';
  return splitName.pop().toLowerCase();
};

export const getFileExtensionFromUrl = (url = '') => {
  const cleanUrl = String(url || '').split('?')[0].split('#')[0];
  const namePart = cleanUrl.split('/').pop() || '';
  return getFileExtension(namePart);
};

export const BOUNDING_BOX_COLORS = [
  'border-red-500 bg-red-500',
  'border-blue-500 bg-blue-500',
  'border-green-500 bg-green-500',
  'border-yellow-500 bg-yellow-500',
  'border-purple-500 bg-purple-500',
  'border-orange-500 bg-orange-500',
  'border-pink-500 bg-pink-500',
  'border-cyan-500 bg-cyan-500',
  'border-indigo-500 bg-indigo-500',
  'border-teal-500 bg-teal-500',
];
